import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { ArenaParticipant } from './useLiveArenas';
import { toast } from '@/hooks/use-toast';

export interface HandRaiseState {
  raisedHands: ArenaParticipant[];
  isHandRaised: boolean;
}

export const useHandRaise = (arenaId: string | null, participants: ArenaParticipant[]) => {
  const { user } = useAuthContext();
  const [isHandRaised, setIsHandRaised] = useState(false);

  // Get participants with raised hands, sorted by when they raised
  const raisedHands = participants
    .filter((p) => p.hand_raised)
    .sort((a, b) => {
      const aTime = a.hand_raised_at ? new Date(a.hand_raised_at).getTime() : 0;
      const bTime = b.hand_raised_at ? new Date(b.hand_raised_at).getTime() : 0;
      return aTime - bTime;
    });

  // Check if current user has hand raised
  useEffect(() => {
    if (user && participants.length > 0) {
      const currentParticipant = participants.find((p) => p.user_id === user.id);
      setIsHandRaised(currentParticipant?.hand_raised || false);
    }
  }, [user, participants]);

  // Raise hand
  const raiseHand = useCallback(async () => {
    if (!user || !arenaId) return false;

    try {
      const { error } = await supabase
        .from('arena_participants')
        .update({
          hand_raised: true,
          hand_raised_at: new Date().toISOString(),
        })
        .eq('arena_id', arenaId)
        .eq('user_id', user.id);

      if (error) throw error;

      setIsHandRaised(true);
      toast({
        title: 'Hand raised! ✋',
        description: 'The host will see your request to speak',
      });
      return true;
    } catch (error) {
      console.error('Error raising hand:', error);
      return false;
    }
  }, [user, arenaId]);

  // Lower hand
  const lowerHand = useCallback(async () => {
    if (!user || !arenaId) return false;

    try {
      const { error } = await supabase
        .from('arena_participants')
        .update({
          hand_raised: false,
          hand_raised_at: null,
        })
        .eq('arena_id', arenaId)
        .eq('user_id', user.id);

      if (error) throw error;

      setIsHandRaised(false);
      return true;
    } catch (error) {
      console.error('Error lowering hand:', error);
      return false;
    }
  }, [user, arenaId]);

  // Toggle hand raise
  const toggleHandRaise = useCallback(async () => {
    if (isHandRaised) {
      return lowerHand();
    } else {
      return raiseHand();
    }
  }, [isHandRaised, raiseHand, lowerHand]);

  // Promote to speaker (host only)
  const promoteToSpeaker = useCallback(
    async (userId: string) => {
      if (!arenaId) return false;

      try {
        const { error } = await supabase
          .from('arena_participants')
          .update({
            is_speaker: true,
            hand_raised: false,
            hand_raised_at: null,
            is_muted: true, // Start muted when promoted
          })
          .eq('arena_id', arenaId)
          .eq('user_id', userId);

        if (error) throw error;

        const participant = participants.find((p) => p.user_id === userId);
        toast({
          title: 'Speaker added! 🎤',
          description: `${participant?.profile?.name || 'User'} is now a speaker`,
        });
        return true;
      } catch (error) {
        console.error('Error promoting to speaker:', error);
        return false;
      }
    },
    [arenaId, participants]
  );

  // Demote to listener (host only)
  const demoteToListener = useCallback(
    async (userId: string) => {
      if (!arenaId) return false;

      try {
        const { error } = await supabase
          .from('arena_participants')
          .update({
            is_speaker: false,
            is_muted: true,
            is_video_on: false,
          })
          .eq('arena_id', arenaId)
          .eq('user_id', userId);

        if (error) throw error;

        const participant = participants.find((p) => p.user_id === userId);
        toast({
          title: 'Moved to listeners',
          description: `${participant?.profile?.name || 'User'} is now a listener`,
        });
        return true;
      } catch (error) {
        console.error('Error demoting to listener:', error);
        return false;
      }
    },
    [arenaId, participants]
  );

  // Dismiss hand raise (host only)
  const dismissHandRaise = useCallback(
    async (userId: string) => {
      if (!arenaId) return false;

      try {
        const { error } = await supabase
          .from('arena_participants')
          .update({
            hand_raised: false,
            hand_raised_at: null,
          })
          .eq('arena_id', arenaId)
          .eq('user_id', userId);

        if (error) throw error;
        return true;
      } catch (error) {
        console.error('Error dismissing hand raise:', error);
        return false;
      }
    },
    [arenaId]
  );

  return {
    raisedHands,
    isHandRaised,
    raiseHand,
    lowerHand,
    toggleHandRaise,
    promoteToSpeaker,
    demoteToListener,
    dismissHandRaise,
  };
};
